import { create } from 'zustand'
import { auth, db } from '../lib/supabase'
import { useGameStore } from './gameStore'

const logAuthStore = () => { }
let loadUserDataPromise = null

export const useAuthStore = create((set, get) => ({
  user: null,
  session: null,
  loading: true,

  initAuth: async () => {
    try {
      set({ loading: true })

      // 1. Récupération de la session initiale
      const { data: sessionData, error: sessionError } = await auth.getSession()

      if (sessionError) {
        console.error('Error getting initial session:', sessionError)
        throw new Error('Failed to get initial session')
      }

      const session = sessionData?.session
      const user = session?.user ?? null

      // 2. Mise à jour de l'état avec la session
      set({
        session,
        user,
        loading: false
      })

      // 3. Configuration de l'écouteur des changements d'authentification
      const { data: { subscription } } = auth.onAuthStateChange(
        async (event, newSession) => {

          const newUser = newSession?.user ?? null
          set({
            session: newSession,
            user: newUser,
            loading: false
          })

          if (newUser) {
            try {
              await get().loadUserData()
            } catch (loadError) {
              console.error('Error loading user data after auth change:', loadError)
            }
          } else {
            // Réinitialisation de l'état du jeu pour les utilisateurs non connectés
            try {
              useGameStore.getState().resetAnonymousGarden()
            } catch (resetError) {
              console.error('Error resetting anonymous garden:', resetError)
            }
          }
        }
      )

      // 5. Nettoyage de l'abonnement lors du démontage
      return () => {
        try {
          subscription?.unsubscribe?.()
        } catch (unsubscribeError) {
          console.error('Error unsubscribing from auth changes:', unsubscribeError)
        }
      }

    } catch (error) {
      console.error('Auth initialization error:', error)
      set({
        loading: false,
        error: error instanceof Error ? error : new Error(String(error))
      })

      // Relancer l'erreur pour qu'elle soit traitée par le composant parent si nécessaire
      throw error
    }
  },

  signUp: async (email, password, username) => {
    try {
      const { data, error } = await auth.signUp(email, password, { username })
      if (error) throw error

      await get().loadUserData()

      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  signIn: async (email, password) => {
    try {
      const { data, error } = await auth.signIn(email, password)
      if (error) throw error

      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  },

  signOut: async () => {
    try {
      const { error } = await auth.signOut()
      if (error) throw error

      set({ user: null, session: null })
      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  resetPassword: async (email) => {
    try {
      const { error } = await auth.resetPassword(email)
      if (error) throw error

      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  updateProfile: async (updates) => {
    try {
      const { user } = get()
      if (!user) throw new Error('No user logged in')

      const { error } = await auth.updateProfile(updates)
      if (error) throw error

      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  // Load user data from Supabase
  loadUserData: async () => {
    if (loadUserDataPromise) {
      return loadUserDataPromise
    }

    loadUserDataPromise = (async () => {
      try {
        // Vérification de la session utilisateur
        const { data: sessionData, error: sessionError } = await auth.getSession()
        if (sessionError) {
          console.error('Session error:', sessionError)
          throw new Error('Failed to get session')
        }

        const user = sessionData?.session?.user
        if (!user?.id) {
          console.warn('No authenticated user found')
          return { data: null, error: 'No authenticated user' }
        }

        // Chargement des données du joueur avec gestion d'erreur robuste
        const playerResult = await db.loadPlayerData(user.id).catch(error => ({
          data: null,
          error: error instanceof Error ? error : new Error(String(error))
        }))

        if (playerResult?.error) {
          console.error('Failed to load player data:', playerResult.error)
          // On continue même en cas d'erreur, on initialise avec des valeurs par défaut
        }
        const playerData = playerResult?.data || null

        // Chargement du profil avec gestion d'erreur robuste
        let profileData = null
        try {
          const profileResult = await db.getProfile(user.id)
          if (profileResult && typeof profileResult === 'object' && !('error' in profileResult)) {
            profileData = profileResult.data || null
          } else if (profileResult?.error) {
            console.warn('Profile not found, will be created on first update')
          }
        } catch (error) {
          console.warn('Error loading profile, will be created on update:', error)
        }

        // Chargement de l'abonnement avec gestion d'erreur robuste
        let subscriptionData = null
        try {
          const subscriptionResult = await db.getSubscription(user.id)
          if (subscriptionResult && typeof subscriptionResult === 'object' &&
            !('error' in subscriptionResult) && subscriptionResult.data) {
            subscriptionData = subscriptionResult.data
          }
        } catch (error) {
          console.warn('Error loading subscription, using default:', error)
        }

        const gameStore = useGameStore.getState()

        const parseTimestamp = (value, fallback = null) => {
          if (!value) return fallback
          const time = new Date(value).getTime()
          return Number.isNaN(time) ? fallback : time
        }

        const defaultState = {
          inventory: [],
          seedInventory: { default: 1 },
          plants: [],
          selectedPlantId: null,
          currentPlant: null,
          plantProgress: 0,
          plantStage: 0,
          pendingSeed: false,
          seedProgress: 0,
          seedClickBonus: 0,
          waterLevel: 100,
          lastWaterRefill: Date.now(),
          waterRefillInterval: 13 * 60 * 60 * 1000,
          waterCooldownUntil: null,
          autoGardenerOwned: false,
          autoGardenerLastTick: null,
          autoGardenerInterval: 10000,
          autoGardenerConfig: { clicksPerTick: 1, intervalMs: 10000 },
          ownedBackgrounds: ['background_garden'],
          selectedBackground: 'background_garden',
          ownedTools: [],
          lastFreeSeedClaim: null,
          fertilizerMultiplier: 1,
          fertilizerActiveUntil: null,
          wateringCanMultiplier: 1,
          herbarium: {},
        }

        if (!playerData) {
          gameStore.setState(defaultState, false)
        } else {
          const safeNumber = (value, fallback) => {
            const num = Number(value)
            return Number.isNaN(num) ? fallback : num
          }

          const resolvedOwnedBackgrounds = Array.isArray(playerData.owned_backgrounds) && playerData.owned_backgrounds.length > 0
            ? playerData.owned_backgrounds
            : defaultState.ownedBackgrounds

          const resolvedSelectedBackground = playerData.selected_background
            || resolvedOwnedBackgrounds[0]
            || defaultState.selectedBackground

          gameStore.setState({
            inventory: playerData.inventory || defaultState.inventory,
            seedInventory: playerData.seed_inventory || defaultState.seedInventory,
            plants: Array.isArray(playerData.plants) ? playerData.plants : defaultState.plants,
            selectedPlantId: playerData.selected_plant_id || defaultState.selectedPlantId,
            currentPlant: playerData.current_plant || defaultState.currentPlant,
            plantProgress: playerData.plant_progress ?? defaultState.plantProgress,
            plantStage: playerData.plant_stage ?? defaultState.plantStage,
            pendingSeed: Boolean(playerData.pending_seed),
            seedProgress: playerData.seed_progress ?? defaultState.seedProgress,
            seedClickBonus: playerData.seed_click_bonus ?? defaultState.seedClickBonus,
            waterLevel: playerData.water_level ?? defaultState.waterLevel,
            lastWaterRefill: parseTimestamp(playerData.last_water_refill, defaultState.lastWaterRefill),
            waterRefillInterval: playerData.water_refill_interval ?? defaultState.waterRefillInterval,
            waterCooldownUntil: parseTimestamp(playerData.water_cooldown_until, defaultState.waterCooldownUntil),
            autoGardenerOwned: Boolean(playerData.auto_gardener_owned),
            autoGardenerLastTick: parseTimestamp(playerData.auto_gardener_last_tick, defaultState.autoGardenerLastTick),
            autoGardenerInterval: playerData.auto_gardener_interval ?? defaultState.autoGardenerInterval,
            autoGardenerConfig: playerData.auto_gardener_config || defaultState.autoGardenerConfig,
            ownedBackgrounds: resolvedOwnedBackgrounds,
            selectedBackground: resolvedSelectedBackground,
            ownedTools: playerData.owned_tools || defaultState.ownedTools,
            lastFreeSeedClaim: parseTimestamp(playerData.last_free_seed_claim, defaultState.lastFreeSeedClaim),
            fertilizerMultiplier: safeNumber(playerData.fertilizer_multiplier, defaultState.fertilizerMultiplier),
            fertilizerActiveUntil: parseTimestamp(playerData.fertilizer_active_until, defaultState.fertilizerActiveUntil),
            wateringCanMultiplier: safeNumber(playerData.watering_can_multiplier, defaultState.wateringCanMultiplier),
            herbarium: playerData.herbarium || defaultState.herbarium,
          }, false)
        }

        if (typeof gameStore.syncHerbariumFromPlants === 'function') {
          try {
            gameStore.syncHerbariumFromPlants()
          } catch (syncError) {
            console.warn('Failed to sync herbarium from plants:', syncError)
          }
        }

        if (subscriptionData) {
          gameStore.setState({
            subscription: {
              status: subscriptionData.status,
              planId: subscriptionData.plan_id,
              currentPeriodEnd: subscriptionData.current_period_end
            }
          }, false)
        }

        logAuthStore('loadUserData:success')
        return { data: { player: playerData, profile: profileData, subscription: subscriptionData }, error: null }
      } catch (error) {
        console.error('Error loading user data:', error)
        logAuthStore('loadUserData:error', error)
        return { data: null, error }
      } finally {
        loadUserDataPromise = null
        logAuthStore('loadUserData:lock:released')
      }
    })()

    return loadUserDataPromise
  },

  // Save user data to Supabase
  saveUserData: async () => {
    const user = await auth.getUser()
    if (!user) return { data: null, error: 'No user logged in' }

    const gameStore = useGameStore.getState()

    try {
      // Prepare player data
      const toISOString = (value) => {
        if (!value && value !== 0) return null
        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date.toISOString()
      }

      const playerData = {
        inventory: gameStore.inventory,
        seedInventory: gameStore.seedInventory,
        plants: gameStore.plants,
        selectedPlantId: gameStore.selectedPlantId,
        currentPlant: gameStore.currentPlant,
        plantProgress: gameStore.plantProgress,
        plantStage: gameStore.plantStage,
        pendingSeed: gameStore.pendingSeed,
        seedProgress: gameStore.seedProgress,
        seedClickBonus: gameStore.seedClickBonus,
        waterLevel: gameStore.waterLevel,
        lastWaterRefill: toISOString(gameStore.lastWaterRefill),
        waterRefillInterval: gameStore.waterRefillInterval,
        waterCooldownUntil: toISOString(gameStore.waterCooldownUntil),
        autoGardenerOwned: gameStore.autoGardenerOwned,
        autoGardenerLastTick: toISOString(gameStore.autoGardenerLastTick),
        autoGardenerInterval: gameStore.autoGardenerInterval,
        autoGardenerConfig: gameStore.autoGardenerConfig,
        ownedBackgrounds: gameStore.ownedBackgrounds,
        selectedBackground: gameStore.selectedBackground,
        ownedTools: gameStore.ownedTools,
        lastFreeSeedClaim: toISOString(gameStore.lastFreeSeedClaim),
        fertilizerMultiplier: gameStore.fertilizerMultiplier,
        fertilizerActiveUntil: toISOString(gameStore.fertilizerActiveUntil),
        wateringCanMultiplier: gameStore.wateringCanMultiplier,
        herbarium: gameStore.herbarium,
      }

      // Save player data
      await db.savePlayerData(user.id, playerData)
      return { data: playerData, error: null }
    } catch (error) {
      console.error('Error saving user data:', error)
      return { data: null, error }
    }
  }
}))
